import { apiGet } from "../client";

/**
 * Get monthly total views data
 * @param {string} timeframe - 'all', 'last_3_months', or 'last_6_months'
 * @param {number} year - Year (default: current year)
 * @returns {Promise<Array>} - Array of monthly view counts
 */
export async function getMonthlyTotalViews(timeframe = 'all', year = new Date().getFullYear()) {
  try {
    console.log('[topProductsViewed] Fetching with timeframe:', timeframe, 'year:', year);

    const response = await apiGet(
      "/api/method/dynesourcerapi.views.get_monthly_total_views",
      { timeframe, year }
    );

    console.log('[topProductsViewed] Raw response:', response);

    // Extract data from Frappe response
    let data = response;
    if (response && typeof response === 'object' && response.message) {
      data = response.message;
    }

    console.log('[topProductsViewed] Extracted data:', data);

    // The API returns structure: { "2025": { "January": 0, "February": 0, ... }, "top_products": [...] }
    // We need to extract the year object and convert to array
    
    if (data && typeof data === 'object') {
      // Find the year key (e.g., "2025")
      const yearKey = String(year);
      
      if (data[yearKey] && typeof data[yearKey] === 'object') {
        console.log('[topProductsViewed] Found year data:', data[yearKey]);
        
        // Month names in the order they should appear
        const monthNames = [
          'January', 'February', 'March', 'April', 'May', 'June',
          'July', 'August', 'September', 'October', 'November', 'December'
        ];
        
        // Extract values in order
        const viewCounts = monthNames.map(month => {
          const value = data[yearKey][month];
          return Number(value || 0);
        });
        
        console.log('[topProductsViewed] Final result:', viewCounts);
        return viewCounts;
      }
      
      // Fallback: try to find any object with month names
      for (const key of Object.keys(data)) {
        const obj = data[key];
        if (obj && typeof obj === 'object' && obj.January !== undefined) {
          console.log('[topProductsViewed] Found month data in key:', key);
          
          const monthNames = [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
          ];
          
          const viewCounts = monthNames.map(month => Number(obj[month] || 0));
          console.log('[topProductsViewed] Final result (fallback):', viewCounts);
          return viewCounts;
        }
      }
    }

    console.warn('[topProductsViewed] Could not extract month data, returning empty array');
    return [];

  } catch (error) {
    console.error('[topProductsViewed] Error:', error);
    return [];
  }
}

/**
 * Map timeframe from dropdown label to API parameter
 * @param {string} rangeLabel - "Last 30 Days", "Last 3 Months", "Last 6 Months"
 * @returns {string} - API timeframe parameter
 */
export function getTimeframeFromRange(rangeLabel) {
  switch (rangeLabel) {
    case "Last 3 Months":
      return "last_3_months";
    case "Last 6 Months":
      return "last_6_months";
    case "Last 30 Days":
      return "all";
    default:
      return "all";
  }
}
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function monthIndexOf(value) {
  if (value == null || value === "") return -1;
  const n = Number(value);
  if (Number.isInteger(n) && n >= 1 && n <= 12) return n - 1;
  const s = String(value).trim().toLowerCase();
  const ymd = s.match(/^\d{4}-(\d{1,2})/);
  if (ymd) return Number(ymd[1]) - 1;
  return MONTH_NAMES.findIndex((m) => m.toLowerCase().startsWith(s.slice(0, 3)) && s.length >= 3);
}

function toProductRow(row) {
  if (row == null) return null;
  if (typeof row !== "object") return { name: String(row), views: 0 };
  const name =
    row.product_name ?? row.product ?? row.mortgage_product ?? row.product_id ?? row.name ?? row.title;
  if (!name) return null;
  const views = Number(
    row.views ?? row.total_views ?? row.view_count ?? row.count ?? row.clicks ?? row.total ?? 0
  );
  return { name: String(name), views: Number.isFinite(views) ? views : 0 };
}

/** Month value → [{ name, views }]: accepts a row, a list of rows, a product name, or a { product: views } map. */
function toProductRows(value) {
  if (value == null) return [];
  if (Array.isArray(value)) return value.map(toProductRow).filter(Boolean);
  if (typeof value !== "object") return [toProductRow(value)].filter(Boolean);
  const single = toProductRow(value);
  if (single) return [single];
  return Object.entries(value)
    .filter(([, v]) => v != null && typeof v !== "object" && Number.isFinite(Number(v)))
    .map(([name, v]) => ({ name, views: Number(v) }));
}

/** Normalise a monthly product payload into 12 buckets of [{ name, views }] (index 0 = January). */
function toMonthlyProductBuckets(response, year) {
  const buckets = MONTH_NAMES.map(() => []);
  let data = response?.message ?? response;
  if (data && typeof data === "object" && !Array.isArray(data) && data[String(year)]) {
    data = data[String(year)];
  }
  if (data && typeof data === "object" && !Array.isArray(data)) {
    const nested = data.months ?? data.monthly ?? data.data ?? data.products;
    if (nested) data = nested;
  }

  if (Array.isArray(data)) {
    for (const row of data) {
      const idx = monthIndexOf(row?.month ?? row?.month_name ?? row?.period ?? row?.month_number);
      const nestedRows = row?.products ?? row?.top_products ?? row?.top_product;
      const rows = nestedRows != null ? toProductRows(nestedRows) : toProductRows(row);
      if (idx >= 0) buckets[idx].push(...rows);
    }
    return buckets;
  }

  if (data && typeof data === "object") {
    for (const [key, value] of Object.entries(data)) {
      const idx = monthIndexOf(key);
      if (idx >= 0) buckets[idx].push(...toProductRows(value));
    }
  }
  return buckets;
}

/**
 * Most viewed product for each month.
 * GET dynesourcerapi.views.get_top_product_monthly_by_timeframe?year=&product_type=
 * @returns {Promise<Array<{name: string, views: number}|null>>} 12 entries, index 0 = January
 */
export async function getTopProductMonthlyByTimeframe({
  year = new Date().getFullYear(),
  productType = "all",
} = {}) {
  const response = await apiGet(
    "/api/method/dynesourcerapi.views.get_top_product_monthly_by_timeframe",
    { year, product_type: productType }
  );
  return toMonthlyProductBuckets(response, year).map((rows) =>
    rows.length ? rows.reduce((best, r) => (r.views > best.views ? r : best), rows[0]) : null
  );
}

/**
 * Top viewed products per month.
 * GET dynesourcerapi.views.get_top_viewed_products_monthly?year=&product_type=
 * @returns {Promise<Array<Array<{name: string, views: number}>>>} 12 buckets, index 0 = January
 */
export async function getTopViewedProductsMonthly({
  year = new Date().getFullYear(),
  productType = "all",
} = {}) {
  const response = await apiGet(
    "/api/method/dynesourcerapi.views.get_top_viewed_products_monthly",
    { year, product_type: productType }
  );
  return toMonthlyProductBuckets(response, year);
}

function sumMonthlyViews(obj) {
  if (!obj || typeof obj !== "object") return 0;
  return MONTH_NAMES.reduce((acc, m) => acc + (Number(obj[m]) || 0), 0);
}

/**
 * Get total views per lender for admin Analytics (same backend as lender portal).
 * When called by admin, backend may return per-lender data (e.g. message.lenders or message[year][lender_user]).
 * @param {Object} params - { timeframe, year, for_admin?: 1 }
 * @returns {Promise<Record<string, number>>} Map of lender_user (or lender email) -> total view count
 */
export async function getMonthlyTotalViewsForAdmin(params = {}) {
  const { timeframe = "all", year = new Date().getFullYear(), for_admin = 1 } = params;
  try {
    const response = await apiGet(
      "/api/method/dynesourcerapi.views.get_monthly_total_views",
      { timeframe, year, for_admin }
    );
    const data = response?.message ?? response;
    if (!data || typeof data !== "object") return {};

    const yearKey = String(year);
    // 1) Backend returns message.lenders = [ { lender_user, total_views } or { lender_user, monthly_views: {...} } ]
    if (Array.isArray(data.lenders)) {
      return data.lenders.reduce((acc, row) => {
        const key = (row.lender_user ?? row.lender_email ?? row.email ?? "").toString().trim().toLowerCase();
        if (!key) return acc;
        const total = row.total_views != null
          ? Number(row.total_views)
          : sumMonthlyViews(row.monthly_views ?? row[yearKey]);
        acc[key] = (acc[key] ?? 0) + total;
        return acc;
      }, {});
    }
    // 2) Backend returns message[year][lender_user] = { January: n, ... }
    if (data[yearKey] && typeof data[yearKey] === "object" && !MONTH_NAMES.includes(Object.keys(data[yearKey])[0])) {
      const byLender = {};
      for (const [lenderKey, monthData] of Object.entries(data[yearKey])) {
        if (monthData && typeof monthData === "object")
          byLender[lenderKey.toString().toLowerCase()] = sumMonthlyViews(monthData);
      }
      return byLender;
    }
    // 3) Single-lender shape (current lender portal): message[year] = { January: n, ... } -> one total, no per-lender
    if (data[yearKey] && typeof data[yearKey] === "object") {
      const total = sumMonthlyViews(data[yearKey]);
      if (total > 0) return { _single: total };
    }
    return {};
  } catch (e) {
    console.warn("[getMonthlyTotalViewsForAdmin]", e);
    return {};
  }
}